/**
 * Picture sync participates in CrewPilot's existing reconnect cycle.
 * The permanent picture id is created before any network call and is reused
 * for storage and the project_pictures row.
 */

import { canAttemptSync } from "./connectivity";
import { OPERATIONAL_LOCK_USER_MESSAGE } from "./operationalLifecycle";
import {
  addProjectPicture,
  addProjectPictureComment,
  createPictureId,
  createThumbnailBlob,
  OFFLINE_NOTE_MESSAGE,
  OFFLINE_PICTURE_MESSAGE,
  validatePictureFile,
} from "./projectPictures";
import { recordSuccessfulSync } from "./syncStatus";
import {
  deleteLocalPicture,
  deleteLocalPictureNote,
  getLocalPicture,
  listLocalPictureNotes,
  listLocalPictures,
  putLocalPicture,
  putLocalPictureNote,
  readLocalPictureBlob,
  readLocalPictureThumb,
  setPictureSyncDraining,
  updateLocalPicture,
  updateLocalPictureNote,
} from "./pictureSyncStore";

export const PICTURE_SYNC_STATE = Object.freeze({
  LOCAL_PENDING: "local_pending",
  UPLOADING_STORAGE: "uploading_storage",
  WRITING_DATABASE: "writing_database",
  SYNCED: "synced",
  NEEDS_ATTENTION: "needs_attention",
});

export const PICTURE_NOT_SAVED_MESSAGE =
  "This picture was not saved on this device. Free some space and try again.";

const BACKOFF_MS = [15000, 30000, 60000, 120000, 300000];

let drainChain = Promise.resolve();

function enqueue(work) {
  const run = drainChain.then(work, work);
  drainChain = run.then(() => undefined, () => undefined);
  return run;
}

function actorScope(actor) {
  return {
    organizationId: actor?.organization_id || null,
    createdBy: actor?.supabase_user_id || null,
  };
}

export function isAttentionPictureSyncError(error) {
  const message = String(error?.message || "");
  return (
    message === OPERATIONAL_LOCK_USER_MESSAGE
    || /archived|not writable|read-only|not on this project|permission|row-level|policy|42501/i.test(message)
  );
}

export function isRetryablePictureSyncError(error) {
  const message = String(error?.message || error || "");
  return (
    message === OFFLINE_PICTURE_MESSAGE
    || message === OFFLINE_NOTE_MESSAGE
    || /offline|network|failed to fetch|load failed|timeout|networkerror/i.test(message)
  );
}

function persistFailure(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  if (name === "QuotaExceededError" || /quota/i.test(message)) {
    return new Error(PICTURE_NOT_SAVED_MESSAGE);
  }
  return new Error("This picture was not saved on this device. Try again.");
}

function fileFromBlob(blob, meta) {
  if (typeof File !== "undefined" && blob instanceof File && blob.name) return blob;
  return new File([blob], meta.file_name || "photo.jpg", {
    type: meta.content_type || blob.type || "image/jpeg",
  });
}

function locationOptions(meta) {
  if (!meta.location_id) return [];
  return [{ project_location_id: meta.location_id, name: meta.location_name || "Location" }];
}

function nextAttempt(retryCount) {
  const delay = BACKOFF_MS[Math.min(Math.max(retryCount, 1) - 1, BACKOFF_MS.length - 1)];
  return new Date(Date.now() + delay).toISOString();
}

async function keepForRetry(meta, error) {
  const retryCount = Number(meta.retry_count || 0) + 1;
  return updateLocalPicture(meta.picture_id, {
    sync_state: PICTURE_SYNC_STATE.LOCAL_PENDING,
    retry_count: retryCount,
    next_attempt_at: nextAttempt(retryCount),
    last_error: String(error?.message || "Sync failed."),
  });
}

async function markAttention(meta, error) {
  return updateLocalPicture(meta.picture_id, {
    sync_state: PICTURE_SYNC_STATE.NEEDS_ATTENTION,
    attention_message: String(error?.message || "This picture needs attention."),
    last_error: String(error?.message || ""),
  });
}

async function flushNotesForPicture(actor, pictureId) {
  const { organizationId, createdBy } = actorScope(actor);
  const notes = await listLocalPictureNotes({ organizationId, createdBy, pictureId });
  for (const note of notes) {
    if (note.sync_state === PICTURE_SYNC_STATE.NEEDS_ATTENTION) continue;
    try {
      await addProjectPictureComment(actor, pictureId, {
        body: note.body,
        source: note.source || "typed",
        commentId: note.comment_id,
        createdAt: note.created_at,
      });
      await deleteLocalPictureNote(note.comment_id);
    } catch (error) {
      if (isAttentionPictureSyncError(error)) {
        await updateLocalPictureNote(note.comment_id, {
          sync_state: PICTURE_SYNC_STATE.NEEDS_ATTENTION,
          attention_message: String(error.message || ""),
        });
      }
      // The picture row is already confirmed. A note failure must not drop it.
    }
  }
}

async function syncPicture(actor, pictureId) {
  const meta = await getLocalPicture(pictureId);
  const { organizationId, createdBy } = actorScope(actor);
  if (!meta || meta.organization_id !== organizationId || meta.created_by !== createdBy) {
    return { skipped: true };
  }

  const blob = await readLocalPictureBlob(pictureId);
  if (!blob) {
    const picture = await markAttention(meta, new Error("The original photo is missing on this device."));
    return { attention: true, picture, message: picture.attention_message };
  }

  const thumb = await readLocalPictureThumb(pictureId);
  let storageAccepted = false;
  try {
    await updateLocalPicture(pictureId, { sync_state: PICTURE_SYNC_STATE.UPLOADING_STORAGE, last_error: "" });
    const saved = await addProjectPicture(actor, meta.project_id, fileFromBlob(blob, meta), {
      pictureId: meta.picture_id,
      locationId: meta.location_id,
      locations: locationOptions(meta),
      capturedAt: meta.captured_at,
      thumbnailBlob: thumb || null,
      onStorageUploaded: () => {
        storageAccepted = true;
        updateLocalPicture(pictureId, {
          sync_state: PICTURE_SYNC_STATE.WRITING_DATABASE,
        }).catch(() => {});
      },
    });
    await flushNotesForPicture(actor, pictureId);
    await deleteLocalPicture(pictureId, { keepNotes: true });
    recordSuccessfulSync(createdBy);
    return { synced: true, picture: { ...meta, ...saved, sync_state: PICTURE_SYNC_STATE.SYNCED } };
  } catch (error) {
    if (isAttentionPictureSyncError(error)) {
      const picture = await markAttention(meta, error);
      return { attention: true, picture, message: picture.attention_message };
    }
    const picture = await keepForRetry(meta, error);
    return { pending: true, picture, retryable: storageAccepted || isRetryablePictureSyncError(error) };
  }
}

export async function saveProjectPicture(actor, projectId, file, options = {}) {
  const validation = validatePictureFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const { organizationId, createdBy } = actorScope(actor);
  if (!organizationId || !createdBy) {
    throw new Error("Organization is required.");
  }

  if (options.locationId && Array.isArray(options.locations)) {
    const known = options.locations.some((row) => row?.project_location_id === options.locationId);
    if (!known) throw new Error("That location is not on this project.");
  }

  const pictureId = createPictureId();
  const capturedAt = new Date().toISOString();
  let thumb = null;
  try {
    thumb = await createThumbnailBlob(file);
  } catch {
    thumb = null;
  }

  const meta = {
    picture_id: pictureId,
    organization_id: organizationId,
    project_id: projectId,
    location_id: options.locationId || null,
    location_name: options.locationName || null,
    created_by: createdBy,
    captured_at: capturedAt,
    local_created_at: capturedAt,
    content_type: validation.contentType,
    file_name: file.name || `photo.jpg`,
    byte_size: file.size || 0,
    sync_state: PICTURE_SYNC_STATE.LOCAL_PENDING,
    retry_count: 0,
    next_attempt_at: null,
    last_error: "",
    attention_message: "",
  };

  try {
    await putLocalPicture({ meta, blob: file, thumb });
  } catch (error) {
    throw persistFailure(error);
  }

  if (!canAttemptSync()) {
    return { pending: true, picture: meta };
  }

  return enqueue(async () => {
    setPictureSyncDraining(true);
    try {
      return await syncPicture(actor, pictureId);
    } finally {
      setPictureSyncDraining(false);
    }
  });
}

export async function drainPendingPictures({ actor, force = false } = {}) {
  if (!canAttemptSync()) return { skipped: "offline" };
  return enqueue(async () => {
    const { organizationId, createdBy } = actorScope(actor);
    const rows = await listLocalPictures({ organizationId, createdBy });
    const due = rows.filter((row) => {
      if (!force && row.sync_state === PICTURE_SYNC_STATE.NEEDS_ATTENTION) return false;
      if (!force && row.next_attempt_at && Date.parse(row.next_attempt_at) > Date.now()) return false;
      return true;
    });
    const notes = await listLocalPictureNotes({ organizationId, createdBy });
    const dueNotes = notes.filter((note) => force || note.sync_state !== PICTURE_SYNC_STATE.NEEDS_ATTENTION);
    if (!due.length && !dueNotes.length) return { synced: 0 };
    setPictureSyncDraining(true);
    let synced = 0;
    try {
      for (const row of due) {
        const result = await syncPicture(actor, row.picture_id);
        if (result?.synced) synced += 1;
      }
      await flushOrphanNotes(actor, { force });
      return { synced };
    } finally {
      setPictureSyncDraining(false);
    }
  });
}

async function flushOrphanNotes(actor, { force = false } = {}) {
  const { organizationId, createdBy } = actorScope(actor);
  const notes = await listLocalPictureNotes({ organizationId, createdBy });
  for (const note of notes) {
    if (!force && note.sync_state === PICTURE_SYNC_STATE.NEEDS_ATTENTION) continue;
    const picture = await getLocalPicture(note.picture_id);
    if (picture) continue;
    try {
      await addProjectPictureComment(actor, note.picture_id, {
        body: note.body,
        source: note.source || "typed",
        commentId: note.comment_id,
        createdAt: note.created_at,
      });
      await deleteLocalPictureNote(note.comment_id);
      recordSuccessfulSync(createdBy);
    } catch (error) {
      if (isAttentionPictureSyncError(error)) {
        await updateLocalPictureNote(note.comment_id, {
          sync_state: PICTURE_SYNC_STATE.NEEDS_ATTENTION,
          attention_message: String(error.message || ""),
        });
      }
    }
  }
}

export async function queuePictureNote(actor, picture, { body, source = "typed" } = {}) {
  const trimmed = String(body || "").trim();
  if (!trimmed) throw new Error("Enter a note.");
  const { organizationId, createdBy } = actorScope(actor);
  if (!organizationId || !createdBy || !picture?.picture_id) {
    throw new Error("This note was not saved.");
  }
  const note = {
    comment_id: createPictureId(),
    picture_id: picture.picture_id,
    organization_id: organizationId,
    project_id: picture.project_id,
    body: trimmed,
    source: source === "voice" ? "voice" : "typed",
    created_by: createdBy,
    created_at: new Date().toISOString(),
    sync_state: PICTURE_SYNC_STATE.LOCAL_PENDING,
    pending_sync: true,
    is_active: true,
  };
  await putLocalPictureNote(note);
  if (canAttemptSync()) {
    const stored = await getLocalPicture(picture.picture_id);
    if (!stored) {
      enqueue(() => flushOrphanNotes(actor, { force: false })).catch(() => {});
    }
  }
  return note;
}

export async function listPendingProjectPictures(actor, projectId) {
  const { organizationId, createdBy } = actorScope(actor);
  const rows = await listLocalPictures({ organizationId, createdBy, projectId });
  return rows.filter((row) => row.sync_state !== PICTURE_SYNC_STATE.SYNCED);
}

export async function countPendingPictures(actor, projectId, locationId) {
  const rows = await listPendingProjectPictures(actor, projectId);
  if (locationId === undefined) return rows.length;
  if (!locationId) return rows.filter((row) => !row.location_id).length;
  return rows.filter((row) => row.location_id === locationId).length;
}

export async function listPendingPictureNotes(actor, pictureId) {
  const { organizationId, createdBy } = actorScope(actor);
  const notes = await listLocalPictureNotes({ organizationId, createdBy, pictureId });
  return notes.map((note) => ({ ...note, pending_sync: true, is_active: true }));
}

export function __resetPictureSyncForTests() {
  drainChain = Promise.resolve();
}
